import { useCallback, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tracker } from 'finhelper';
import { routes } from '#app/constants/routes';
import { handleDeepLink, isMobileDevice } from '#app/helpers/deepLinkHandler';
import useAppMetricaLinks from './useAppMetricaLinks';

const useMainPage = ({ auth, handleRegisterModal, handleTitle, i18n }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { appStoreUrl } = useAppMetricaLinks();

  const [state, setState] = useState({
    isScrolled: false,
    isAgreementModalOpen: false,
    isPolicyModalOpen: false,
  });

  // Обработчик прокрутки
  const handleScroll = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isScrolled: window.pageYOffset > 0,
    }));
  }, []);

  // Обработчик регистрации
  const handleRegisterClick = useCallback(
    (e) => {
      e.preventDefault();
      console.log('[MainPage] Register button clicked', { auth, appStoreUrl });

      if (auth) {
        console.log('[MainPage] User is authenticated, navigating to cabinet');
        navigate(routes.CABINET);
      } else {
        Tracker.reachGoal('register_click');
        // Если мобильное устройство - пытаемся открыть deep link
        if (isMobileDevice()) {
          console.log('[MainPage] Mobile device detected, attempting deep link');
          const handled = handleDeepLink(appStoreUrl);
          // Если deep link не обработан (не мобилка или ошибка), открываем модалку
          if (!handled) {
            console.log('[MainPage] Deep link not handled, opening register modal');
            handleRegisterModal();
          }
        } else {
          // Десктоп - просто открываем модалку
          console.log('[MainPage] Desktop device, opening register modal');
          handleRegisterModal();
        }
      }
    },
    [auth, navigate, handleRegisterModal, appStoreUrl]
  );

  // Универсальный обработчик модальных окон
  const handleModalToggle = useCallback((modalName, isOpen) => {
    setState((prev) => ({
      ...prev,
      [modalName]: isOpen,
    }));
  }, []);

  // Обработчики для конкретных модальных окон
  const handleAgreementClick = useCallback(
    (e) => {
      e.preventDefault();
      handleModalToggle('isAgreementModalOpen', true);
    },
    [handleModalToggle]
  );

  const handlePolicyClick = useCallback(
    (e) => {
      e.preventDefault();
      handleModalToggle('isPolicyModalOpen', true);
    },
    [handleModalToggle]
  );

  const handleAgreementClose = useCallback(() => {
    handleModalToggle('isAgreementModalOpen', false);
  }, [handleModalToggle]);

  const handlePolicyClose = useCallback(() => {
    handleModalToggle('isPolicyModalOpen', false);
  }, [handleModalToggle]);

  // Эффект инициализации
  useLayoutEffect(() => {
    handleTitle(i18n.mainPage.title);
    document.addEventListener('scroll', handleScroll);

    // Обработка роутинга для модальных окон
    if (location.pathname === '/agreement') {
      setState((prev) => ({ ...prev, isAgreementModalOpen: true }));
    }
    if (location.pathname === '/policy') {
      setState((prev) => ({ ...prev, isPolicyModalOpen: true }));
    }

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [location, handleTitle, i18n.mainPage.title, handleScroll]);

  return {
    state,
    handlers: {
      handleRegisterClick,
      handleAgreementClick,
      handlePolicyClick,
      handleAgreementClose,
      handlePolicyClose,
    },
  };
};

export default useMainPage;
